import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
    FOCUS_HOLD_MS,
    findFocusedMessage,
    focusKeyFor,
    holdFocusedMessage,
    isFocusConsumed,
    markFocusConsumed,
} from '../lib/bellLinkage';

/**
 * The bell hand-off still active for this mount, or null: `focusCommId` from
 * router state unless an EARLIER mount of the same history entry consumed it
 * (a refresh, Back). Read once per hand-off, so applying the focus during
 * this mount does not deactivate it here. Exported separately because the
 * personal reader needs it before its message list exists (to start the
 * focused card expanded), earlier than `useBellFocus` can run.
 */
export function useActiveBellFocus() {
    const location = useLocation();
    const focusCommId = location.state?.focusCommId || null;
    const focusKey = focusKeyFor(location.key, focusCommId);
    const stale = useMemo(() => isFocusConsumed(focusKey), [focusKey]);
    return { activeFocus: stale ? null : focusCommId, focusKey, locationKey: location.key };
}

/**
 * #715 — open a thread reader at the message the bell entry named.
 *
 * The bell navigates with `location.state.focusCommId`. Both readers
 * (`EmailDetailPage`, `PersonalThreadView`) render every card with
 * `data-comm-id` inside one scroll container; this hook scrolls the named card
 * into view, and otherwise runs the reader's own `onNoFocus` (open at the
 * newest message). Returns the focus that is still ACTIVE for this mount — the
 * personal reader uses it to start that card expanded.
 *
 * The rules, each from an audit finding:
 *
 * - **Once per navigation (F01).** The scroll decision re-runs only when the
 *   thread or its message count changes. A reply's optimistic card or an SSE
 *   refetch grows the count; that goes to `onNoFocus`, never back up to the
 *   bell's message.
 * - **Once per history entry (F05).** Router state survives a refresh and Back,
 *   so an applied hand-off is recorded (`markFocusConsumed`) and a later mount
 *   of the same entry treats it as absent.
 * - **Held while bodies settle (F04).** The iframes above the card grow after
 *   load; `holdFocusedMessage` keeps the card in place for a few seconds unless
 *   the user scrolls.
 * - **Idempotent across effect re-runs.** React StrictMode mounts effects
 *   twice in development. A re-run with the same thread and count makes no new
 *   decision; it only resumes a hold the cleanup interrupted.
 */
export default function useBellFocus({ scrollRef, threadKey, ready, count, onNoFocus }) {
    const { activeFocus, focusKey, locationKey } = useActiveBellFocus();

    const onNoFocusRef = useRef(onNoFocus);
    onNoFocusRef.current = onNoFocus;
    const appliedRef = useRef(null);
    const decisionRef = useRef(null);
    const holdUntilRef = useRef(0);

    useEffect(() => {
        const node = scrollRef.current;
        if (!ready || !count || !node) return undefined;
        const decision = `${threadKey}|${count}`;
        const applyKey = activeFocus ? `${locationKey}:${activeFocus}` : null;

        if (applyKey && appliedRef.current !== applyKey) {
            const card = findFocusedMessage(node, activeFocus);
            if (card) {
                appliedRef.current = applyKey;
                decisionRef.current = decision;
                markFocusConsumed(focusKey);
                card.scrollIntoView({ block: 'start' });
                holdUntilRef.current = Date.now() + FOCUS_HOLD_MS;
                return holdFocusedMessage(node, card);
            }
        }

        if (decisionRef.current === decision) {
            // Nothing new to decide — resume a hold a cleanup cut short.
            const remaining = holdUntilRef.current - Date.now();
            const card = remaining > 0 && activeFocus ? findFocusedMessage(node, activeFocus) : null;
            return card ? holdFocusedMessage(node, card, remaining) : undefined;
        }

        decisionRef.current = decision;
        holdUntilRef.current = 0;
        onNoFocusRef.current?.(node);
        return undefined;
    }, [scrollRef, threadKey, ready, count, activeFocus, focusKey, locationKey]);

    return activeFocus;
}
